import { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  packNotificationAction,
  schoolDetailAction,
} from "../../Redux/Action/authAction";
import { organisationInvitesListAction } from "../../Redux/Action/organisationAction";
import { getMemberSidebarConfig, getOrganisationSidebarConfig, getAccountSidebarConfig } from "../../config/sidebarConfig";
import { getOrganisationRoleAccess, isOrganisationGroupUser, getActiveGroup } from "../../OrganisationGroups/utils/roleAccess";
import SchoolInvitesOffcanvas from "./SchoolInvitesOffcanvas";
import { isTrialEndedReadOnly } from "../../Util/HelperFunction";

const getTabDescription = (tab) => {
  switch (tab) {
    case "Dashboard":
      return "The dashboard provides a complete overview of your organisation's activity, engagement, and key statistics.";
    case "Collections":
      return "Create, manage, and share curated sets of videos and resources for your learners. Organise content by subjects, topics, or specific learning goals.";
    case "Strategy Packs":
      return "Access comprehensive, ready-to-use strategy packs designed to support teaching and learning. Discover expert-curated materials to enhance your curriculum.";
    case "Learners":
    case "Students":
      return "Manage your student roster, track individual progress, and assign specific resources. Keep detailed records of learner engagement and performance.";
    case "Parents":
      return "Connect with parents and guardians to share updates, resources, and progress. Foster a collaborative environment between school and home.";
    case "Worker":
    case "Workers":
    case "Support Workers":
      return "Manage support workers, track activities, and view engagement details.";
    case "Classes":
      return "Organise your learners into classes and manage them efficiently as a group. Assign collections and strategy packs to entire classes with ease.";
    case "Manage Admin":
      return "Control access and permissions for administrative staff within your organisation. Add, remove, and manage admin roles securely.";
    case "Resource Mapping":
      return "Explore detailed guides and frameworks that map our content to your curriculum. Align resources precisely with educational standards and requirements.";
    default:
      return "Access this powerful feature to manage and enhance your educational experience.";
  }
};

export default function Sidebar({ isActive, setIsActive, setShow, show }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [showInvitesOffcanvas, setShowInvitesOffcanvas] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [trialModalTab, setTrialModalTab] = useState("");
  const [showUpgradeContactModal, setShowUpgradeContactModal] = useState(false);

  const handleTrialClick = (e, tabName) => {
    e.preventDefault();
    setTrialModalTab(tabName);
    setShowTrialModal(true);
  };

  const schoolData = useSelector((state) => state?.authSlice?.schoolDetail);
  const schoolDetailStatus = useSelector((state) => state?.authSlice?.schoolDetailStatus);
  const Notification = useSelector((state) => state?.authSlice?.notification);
  const notificationStatus = useSelector((state) => state?.authSlice?.notificationStatus);
  const userData = useSelector((state) => state?.authSlice.userData?.payload);

  const [orgSectionOpen, setOrgSectionOpen] = useState(false);
  const [memberSectionOpen, setMemberSectionOpen] = useState(false);
  const [accountSectionOpen, setAccountSectionOpen] = useState(false);
  const [viewedInviteIds, setViewedInviteIds] = useState(() => {
    try {
      const stored = sessionStorage.getItem("viewedInviteIds");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [currentPendingIds, setCurrentPendingIds] = useState([]);

  const hasPendingInvite = currentPendingIds.some((id) => !viewedInviteIds.includes(id));

  useEffect(() => {
    const handleInvitesViewed = () => {
      setViewedInviteIds((prev) => {
        const newIds = Array.from(new Set([...prev, ...currentPendingIds]));
        sessionStorage.setItem("viewedInviteIds", JSON.stringify(newIds));
        return newIds;
      });
    };
    window.addEventListener("invites-viewed", handleInvitesViewed);
    return () => window.removeEventListener("invites-viewed", handleInvitesViewed);
  }, [currentPendingIds]);

  const access = getOrganisationRoleAccess(userData, schoolData);


  const isDualAccess = access.hasMemberAccess && access.isGroupUser && !access.hasNoPermissions;

  useEffect(() => {
    if (isDualAccess) {
      const isAccountRoute = ["/settings", "/help-support", "/report-issue"].some(path => location.pathname.includes(path));

      if (isAccountRoute) {
        setOrgSectionOpen(false);
        setMemberSectionOpen(false);
        setAccountSectionOpen(true);
      } else if (location.pathname.startsWith("/organisations")) {
        setOrgSectionOpen(true);
        setMemberSectionOpen(false);
        setAccountSectionOpen(false);
      } else {
        setOrgSectionOpen(false);
        setMemberSectionOpen(true);
        setAccountSectionOpen(false);
      }
    } else {
      setOrgSectionOpen(true);
      setMemberSectionOpen(true);
      setAccountSectionOpen(true);
    }
  }, [isDualAccess, location.pathname]);

  const gotoDashBoard = () => {
    if (access.hasMemberAccess && !location.pathname.startsWith("/organisations")) {
      navigate(`/dashboard`);
    } else if (access.isGroupUser) {
      navigate(`/organisations/dashboard`);
    } else {
      navigate(`/dashboard`);
    }
  };

  useEffect(() => {
    if (!userData?._id || isOrganisationGroupUser(userData)) return;

    if (schoolDetailStatus === "idle") {
      dispatch(schoolDetailAction());
    }

    if (userData?.role === "School" && notificationStatus === "idle") {
      dispatch(packNotificationAction());
    }
  }, [dispatch, notificationStatus, schoolDetailStatus, userData]);

  useEffect(() => {
    if (userData?.role === "School") {
      dispatch(organisationInvitesListAction({ page: 0, limit: 10, search: "" }))
        .then((response) => {
          if (response?.payload?.data?.invites) {
            const pendingInvites = response.payload.data.invites.filter(
              (invite) => invite.inviteStatus === "INVITED"
            );
            setCurrentPendingIds(pendingInvites.map((i) => i._id));
          }
        });
    }
  }, [dispatch, userData?.role, schoolDetailStatus]);

  useEffect(() => {
    if (showInvitesOffcanvas) {
      setViewedInviteIds((prev) => {
        const newIds = Array.from(new Set([...prev, ...currentPendingIds]));
        sessionStorage.setItem("viewedInviteIds", JSON.stringify(newIds));
        return newIds;
      });
    }
  }, [showInvitesOffcanvas, currentPendingIds]);

  const hasUnseenInvite = hasPendingInvite;

  const memberConfig = getMemberSidebarConfig({
    access,
    userData,
    schoolData,
    Notification,
    showInvitesOffcanvas,
    setShowInvitesOffcanvas,
    hasPendingInvite,
    hasUnseenInvite,
  });

  const orgConfig = getOrganisationSidebarConfig({ access });

  const isOrgRoute = location.pathname.startsWith("/organisations");
  const accountConfig = getAccountSidebarConfig({ access, isOrgRoute });

  const renderMenuItems = (config) => {
    return config.filter(item => item.show).map((item, idx) => {
      const isNoSpace = ["Collections", "Strategy Packs", "Parents", "Help & Support", "Settings"].includes(item.label);
      
      const isTrialMode = schoolData?.isTrial || schoolData?.schoolId?.trialInfo?.isTrial || schoolData?.trialInfo?.isTrial;
      const isTrialEnded = isTrialEndedReadOnly(schoolData, userData);
      const isTrialDisabled = isTrialMode && (userData?.role === "School" || userData?.role === "Teacher") && !["Browse", "Teachers", "Manage Organisation", "Help & Support", "Settings", "Strategy Packs", "Resource Mapping"].includes(item.label);
      const disabledStyle = isTrialDisabled ? { opacity: 0.4, filter: "grayscale(100%)" } : {};

      if (item.isButton) {
        return (
          <div className={isNoSpace ? "" : "side-btm-space"} key={idx} style={disabledStyle}>
            <button
              type="button"
              className={item.isActive ? "menu-btn bar_active" : "menu-btn"}
              onClick={isTrialDisabled ? (e) => handleTrialClick(e, item.label) : item.onClick}
            >
              {item.icon}
              <p>
                <span style={{ position: "relative" }}>
                  {item.label}
                  {item.notificationDotYellow && <span className="sidebar-notification-dot-yellow"></span>}
                </span>
              </p>
            </button>
          </div>
        );
      }

      if (item.isExternal) {
        return (
          <div className={isNoSpace ? "" : "side-btm-space"} key={idx} style={disabledStyle}>
            <Link
              to={isTrialDisabled ? "#" : item.path}
              target={isTrialDisabled ? undefined : "_blank"}
              rel="noopener noreferrer"
              onClick={isTrialDisabled ? (e) => handleTrialClick(e, item.label) : undefined}
              className={location.pathname === item.path ? "menu-btn bar_active" : "menu-btn"}
            >
              {item.icon}
              <p>{item.label}</p>
            </Link>
          </div>
        );
      }

      const isActiveRoute = item.activePaths
        ? item.activePaths.includes(location.pathname)
        : location.pathname === item.path;

      return (
        <div className={isNoSpace ? "" : "side-btm-space"} key={idx} style={disabledStyle}>
          <Link
            to={isTrialDisabled ? "#" : item.path}
            onClick={isTrialDisabled ? (e) => handleTrialClick(e, item.label) : undefined}
            className={isActiveRoute ? "menu-btn bar_active" : "menu-btn"}
          >
            {item.icon}
            <p>
              <span style={{ position: "relative" }}>
                {item.label}
                {item.notificationDot && <span className="sidebar-notification-dot"></span>}
              </span>
            </p>
          </Link>
        </div>
      );
    });
  };

  return (
    <aside>
      <div className="sidebar">
        <div className="toggle-icon" onClick={() => setShow(!show)}>
          <img src={require("../../Assets/Images/toggle.svg").default} alt="Toggle sidebar" />
        </div>
        <div
          className="logo-side"
          onClick={gotoDashBoard}
          style={{ cursor: "pointer" }}
        >
          <img
            className="small"
            src={require("../../Assets/Images/clogo.png")}
            alt="Logo small"
          />
          <img
            className="big"
            src={require("../../Assets/Images/main-logo.png")}
            alt="Logo big"
          />
        </div>
        <div className="side-menu">

          {access.hasMemberAccess && (
            <div className="sidebar-section-container">
              {!show && isDualAccess && (
                <div
                  className="organisation-sidebar-section collapsible-section-header"
                  onClick={() => setMemberSectionOpen(!memberSectionOpen)}
                  style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}
                >
                  <p style={{ margin: 0, fontWeight: "bold", color: "#8b9eb7", fontSize: "13px", textTransform: "uppercase" }}>{schoolData?.schoolName || userData?.schoolName || "Your Organisation"}</p>
                  <span style={{ fontSize: "12px", color: "#666" }}>{memberSectionOpen ? "▲" : "▼"}</span>
                </div>
              )}
              {(!isDualAccess || memberSectionOpen || show) && (
                <div>
                  {renderMenuItems(memberConfig)}
                </div>
              )}
            </div>
          )}

          {access.isGroupUser && !access.hasNoPermissions && (
            <div className="sidebar-section-container" style={{ marginTop: access.hasMemberAccess && !show ? '20px' : '0' }}>
              {!show && (
                <div
                  className="organisation-sidebar-section collapsible-section-header"
                  onClick={() => isDualAccess && setOrgSectionOpen(!orgSectionOpen)}
                  style={{ cursor: isDualAccess ? "pointer" : "default", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}
                >
                  <p style={{ margin: 0, fontWeight: "bold", color: "#8b9eb7", fontSize: "13px", textTransform: "uppercase" }}>{getActiveGroup(userData)?.name || "Your Organisation Group"}</p>
                  {isDualAccess && <span style={{ fontSize: "12px", color: "#666" }}>{orgSectionOpen ? "▲" : "▼"}</span>}
                </div>
              )}
              {(!isDualAccess || orgSectionOpen || show) && (
                <div>
                  {renderMenuItems(orgConfig)}
                </div>
              )}
            </div>
          )}


          <div className="sidebar-section-container" style={{ marginTop: isDualAccess && !show ? '20px' : 'auto' }}>
            {!show && (
              <div
                className="organisation-sidebar-section collapsible-section-header"
                onClick={() => isDualAccess && setAccountSectionOpen(!accountSectionOpen)}
                style={{ cursor: isDualAccess ? "pointer" : "default", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}
              >
                <p style={{ margin: 0, fontWeight: "bold", color: "#8b9eb7", fontSize: "13px", textTransform: "uppercase" }}>Your Account</p>
                {isDualAccess && <span style={{ fontSize: "12px", color: "#666" }}>{accountSectionOpen ? "▲" : "▼"}</span>}
              </div>
            )}
            {(!isDualAccess || accountSectionOpen || show) && (
              <div>
                {renderMenuItems(accountConfig)}
              </div>
            )}
          </div>

        </div>
      </div>
      <SchoolInvitesOffcanvas
        show={showInvitesOffcanvas}
        handleClose={() => setShowInvitesOffcanvas(false)}
      />
      <Modal show={showTrialModal} onHide={() => setShowTrialModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: "none" }}>
        </Modal.Header>
        <Modal.Body className="text-center pb-5 pt-2">
          <div className="mb-3">
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z" fill="#ff9800" fillOpacity="0.2"/>
              <path d="M12 8V13" stroke="#ff9800" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M11.9941 16H12.0031" stroke="#ff9800" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h3 className="mb-3 px-3" style={{ color: "var(--primary)", fontWeight: "600", wordBreak: "break-word", overflowWrap: "break-word", whiteSpace: "normal", fontSize: "22px" }}>
            {trialModalTab ? trialModalTab.charAt(0).toUpperCase() + trialModalTab.slice(1).toLowerCase() : ""} not available during your trial
          </h3>
          <p className="mb-4 px-3" style={{ color: "#666", wordBreak: "break-word", overflowWrap: "break-word", whiteSpace: "normal", fontSize: "14px" }}>
            {getTabDescription(trialModalTab)} Please upgrade your trial organisation to a full account.
          </p>
          <button 
            className="submit" 
            onClick={() => {
              setShowTrialModal(false);
              setShowUpgradeContactModal(true);
            }}
            style={{ width: "100%", maxWidth: "220px", borderRadius: "8px" }}
          >
            Upgrade account
          </button>
        </Modal.Body>
      </Modal>

      <Modal show={showUpgradeContactModal} onHide={() => setShowUpgradeContactModal(false)} centered className="upgrade-contact-modal">
        <Modal.Header closeButton style={{ borderBottom: "none", paddingBottom: 0 }}>
        </Modal.Header>
        <Modal.Body className="text-center px-4 pb-5 pt-0">
          <div className="mb-4 d-flex justify-content-center">
            <div style={{ width: "72px", height: "72px", borderRadius: "50%", background: "rgba(223, 27, 136, 0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 16L22 4L16 8L12 3L8 8L2 4L5 16H19ZM5 18V20H19V18H5Z" fill="var(--accent-pink)"/>
              </svg>
            </div>
          </div>
          <h3 className="mb-3" style={{ color: "var(--primary)", fontWeight: "700", fontSize: "24px" }}>
            Upgrade to Full Account
          </h3>
          <p className="mb-4" style={{ color: "#555", fontSize: "15px", lineHeight: "1.6", padding: "0 10px" }}>
            Please contact the Administrator to upgrade your organisation's account and unlock all features.
          </p>
          
          <a 
            href="mailto:info@yipiyap.co.uk" 
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              background: "#f8f9fa",
              border: "1px solid #e9ecef",
              padding: "14px 24px",
              borderRadius: "12px",
              color: "var(--primary)",
              textDecoration: "none",
              fontWeight: "600",
              fontSize: "16px",
              marginBottom: "28px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6ZM20 6L12 11L4 6H20ZM20 18H4V8L12 13L20 8V18Z" fill="var(--accent-pink)"/>
            </svg>
            info@yipiyap.co.uk
          </a>

          <div className="d-flex justify-content-center">
            <button 
              className="submit" 
              onClick={() => setShowUpgradeContactModal(false)}
              style={{ width: "100%", maxWidth: "200px", borderRadius: "10px", fontWeight: "600", padding: "12px 0", letterSpacing: "0.5px" }}
            >
              Got it
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </aside>
  );
}
